import { clsx } from 'clsx';
import { useInput, useNotify } from 'react-admin';
import { CheckCircleFilled, LinkOutlined } from '@ant-design/icons';

import { CxCreditQuestion } from '@types';
import { LightTooltip, SelectInput, TextInput } from '@components';
import cls from './Questions.module.css';

interface Props {
  title: string;
  source: string;
  isSurveyCompleted: boolean;
  link: string | null;
}

interface QProps {
  item: CxCreditQuestion;
  idx: number;
  source: string;
  disabled?: boolean;
}

const QuestionSelect = ({ idx, item, source, disabled }: QProps) => {
  return (
    <div className={cls.row}>
      <div className={clsx(cls.label, cls.labelSelect)}>{idx + 1}. {item.name}</div>
      <div className={clsx(cls.field, cls.fielSelect)}>
        <SelectInput
          source={`${source}[${idx}].answer`}
          label={false}
          choices={item.options.map(o => ({ id: o.value, name: o.label }))}
          className={cls.select}
          emptyValue={false}
          readOnly={disabled}
        />
      </div>
      <div className={cls.owner}>
        {item.answeredByUser && (
          <LightTooltip extraWidth className={cls.tooltipInfo} title="Answer provided by user">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
              <path d="M10 0C4.48421 0 0 4.48421 0 10C0 15.5158 4.48421 20 10 20C15.5158 20 20 15.5158 20 10C20 4.48421 15.5158 0 10 0ZM11.8105 14.0421C11.6 14.5684 11.1158 15.4737 10.1474 16C9.55789 16.3158 9.13684 16.2947 9.03158 16.2947C8.90526 16.2947 8.06316 16.2526 7.70526 15.5789C7.53684 15.2211 7.57895 14.8632 7.6 14.7158L9.17895 8.90526H8.37895C8.27368 8.90526 8.18947 8.8421 8.16842 8.73684L8.12632 8.4C8.10526 8.29474 8.18947 8.18947 8.29474 8.16842L11.5579 7.62105C11.7053 7.6 11.8316 7.72632 11.7895 7.87368L10.0632 14.2947C10 14.5684 10.3368 14.8 10.5684 14.5895C10.8842 14.2947 11.0947 13.9789 11.2211 13.7263C11.2842 13.6211 11.4105 13.5789 11.5158 13.6421C11.6 13.6842 11.6632 13.7474 11.7474 13.7895C11.8316 13.8316 11.8526 13.9368 11.8105 14.0421ZM11.0105 6.52632C10.2316 6.52632 9.6 5.89474 9.6 5.11579C9.6 4.33684 10.2316 3.70526 11.0105 3.70526C11.7895 3.70526 12.4211 4.33684 12.4211 5.11579C12.4211 5.89474 11.7895 6.52632 11.0105 6.52632Z" fill="#042E6B" />
            </svg>
          </LightTooltip>
        )}
      </div>
    </div>
  );
};

const QuestionText = ({ idx, item, source, disabled }: QProps) => {
  return (
    <div className={cls.row}>
      <div className={clsx(cls.label, cls.labelText)}>{idx + 1}. {item.name}</div>
      <div className={clsx(cls.field, cls.fieldText)}>
        <TextInput
          source={`${source}[${idx}].answer`}
          label={false}
          className={cls.text}
          multiline
          // disabled={disabled}
          readOnly={disabled}
          rows={2}
          sx={{
            borderBottom: 'none',
            paddingBottom: '0',
            '& textarea': {
              lineHeight: '2em',
              backgroundImage: 'linear-gradient(to bottom, transparent 97%, #000 100%)',
              backgroundSize: '100% 2em',
            },
          }}
        />
      </div>
      <div className={cls.owner}>
        {item.answeredByUser && (
          <LightTooltip extraWidth className={cls.tooltipInfo} title="Answer provided by user">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
              <path d="M10 0C4.48421 0 0 4.48421 0 10C0 15.5158 4.48421 20 10 20C15.5158 20 20 15.5158 20 10C20 4.48421 15.5158 0 10 0ZM11.8105 14.0421C11.6 14.5684 11.1158 15.4737 10.1474 16C9.55789 16.3158 9.13684 16.2947 9.03158 16.2947C8.90526 16.2947 8.06316 16.2526 7.70526 15.5789C7.53684 15.2211 7.57895 14.8632 7.6 14.7158L9.17895 8.90526H8.37895C8.27368 8.90526 8.18947 8.8421 8.16842 8.73684L8.12632 8.4C8.10526 8.29474 8.18947 8.18947 8.29474 8.16842L11.5579 7.62105C11.7053 7.6 11.8316 7.72632 11.7895 7.87368L10.0632 14.2947C10 14.5684 10.3368 14.8 10.5684 14.5895C10.8842 14.2947 11.0947 13.9789 11.2211 13.7263C11.2842 13.6211 11.4105 13.5789 11.5158 13.6421C11.6 13.6842 11.6632 13.7474 11.7474 13.7895C11.8316 13.8316 11.8526 13.9368 11.8105 14.0421ZM11.0105 6.52632C10.2316 6.52632 9.6 5.89474 9.6 5.11579C9.6 4.33684 10.2316 3.70526 11.0105 3.70526C11.7895 3.70526 12.4211 4.33684 12.4211 5.11579C12.4211 5.89474 11.7895 6.52632 11.0105 6.52632Z" fill="#042E6B" />
            </svg>
          </LightTooltip>
        )}
      </div>
    </div>
  );
};

export const Questions = ({ title, link, isSurveyCompleted, source }: Props) => {
  const notify = useNotify();
  // const { field: { value: questions } } = useInput({ source });
  const { field } = useInput({ source });
  const questions = field.value ?? [];

  // const questions = useWatch({ name: source }) ?? [];

  const onCopy = async () => {
    if (link) {
      try {
        await navigator.clipboard.writeText(link);
        notify('Link copied to clipboard');
      } catch (err) {
        console.error('Error copying to clipboard:\n', err);
      }
    }
  };

  return (
    <div className={cls._}>
      <div className={cls.head}>
        <div className={cls.title}>{title}</div>
        <div className={cls.icons}>
          {isSurveyCompleted && (
            <div className={cls.surveyCheck}>
              <CheckCircleFilled />
            </div>
          )}
          {link && (
            <div className={cls.surveyLink} onClick={onCopy}>
              <LinkOutlined />
            </div>
          )}
        </div>
      </div>
      <div className={cls.wrap}>
        {questions?.length ? (
          questions
            .map((item: CxCreditQuestion, index: number) => (
              item.type !== 'text'
                ? <QuestionSelect
                  key={item.id || index}
                  idx={index}
                  item={item}
                  source={source}
                  disabled={item.answeredByUser}
                />
                : <QuestionText
                  key={item.id || index}
                  idx={index}
                  item={item}
                  source={source}
                  disabled={item.answeredByUser}
                />
            ))) : (
          <div className={cls.empty}>Empty</div>
        )}
      </div>
    </div>
  );
};
